import { useEffect, useMemo, useState } from 'react';
import { Alert, Chip, LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { FerramentasDaListagem, LinhasEsqueletoTabela } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { IListagemOrdemServico, OrdemServicoService } from '../../../shared/services/api/servicos/OrdemServicoService';
import { CelulaListaMobile, LinhaListagemMobile, ListaMobile, useCelular, moedaMobile } from '../../../shared/components/listagem-mobile';

const COR_STATUS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  ABERTA: 'info',
  EM_DIAGNOSTICO: 'warning',
  AGUARDANDO_APROVACAO: 'warning',
  APROVADA: 'info',
  EM_EXECUCAO: 'warning',
  AGUARDANDO_PECA: 'warning',
  CONCLUIDA: 'success',
  ENTREGUE: 'success',
  CANCELADA: 'error',
};

export const ListagemDeOrdensServico: React.FC = () => {
  const listaCelular = useCelular();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();

  const [rows, setRows] = useState<IListagemOrdemServico[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };

  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      OrdemServicoService.getAll(pagina, busca)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [busca, pagina]);

  return (
    <LayoutBaseDePagina
      titulo='Ordens de Serviço'
      barraDeFerramentas={
        <FerramentasDaListagem
          mostrarInputBusca
          textoDaBusca={busca}
          textoBotaoNovo='Nova OS'
          aoClicarEmNovo={() => navigate('/ordensservico/novo')}
          aoMudarTextoDeBusca={texto => setSearchParams({ busca: texto, pagina: '1' }, { replace: true })}
        />
      }
    >
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg} autoHideDuration={tms === 'error' ? null : 900} onClose={handleCloseMsg}>
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>
      <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
        <Table>
          {!listaCelular && (
          <TableHead>
            <TableRow>
              <TableCell>Nº</TableCell>
              <TableCell>Emissão</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Bem</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>Total</TableCell>
            </TableRow>
          </TableHead>
          )}

          {isLoading && rows.length === 0 && (
            <TableBody>
              <LinhasEsqueletoTabela
                colunas={[
                  { width: '20%' },
                  { width: '40%' },
                  { width: '60%' },
                  { width: '55%' },
                  { width: '40%' },
                  { align: 'right', width: '35%' },
                ]}
              />
            </TableBody>
          )}
          <TableBody>
            {listaCelular ? (
              <CelulaListaMobile>
                <ListaMobile
                  linhas={rows}
                  chave={(row) => row.idos}
                  renderizar={(row) => (
                    <LinhaListagemMobile
                      avatar={row.nomeparceiro || '?'}
                      titulo={row.nomeparceiro || 'Sem cliente'}
                      detalhes={[dayjs(row.emissao).format('DD/MM/YY'), `Nº ${row.numero}`, row.descricaobem]}
                      valor={moedaMobile(row.total)}
                      corValor="success.main"
                      etiqueta={{ rotulo: String(row.status), cor: COR_STATUS[row.status] ?? 'default' }}
                      onClick={() => navigate(`/ordensservico/${row.idos}`)}
                    />
                  )}
                />
              </CelulaListaMobile>
            ) : rows.map(row => (
              <TableRow key={row.idos}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/ordensservico/${row.idos}`)}
              >
                <TableCell>{row.numero}</TableCell>
                <TableCell>{dayjs(row.emissao).format('DD/MM/YYYY')}</TableCell>
                <TableCell>{row.nomeparceiro}</TableCell>
                <TableCell>{row.descricaobem}{row.placa ? ` (${row.placa})` : row.patrimonio ? ` (${row.patrimonio})` : ''}</TableCell>
                <TableCell>
                  <Chip label={row.status} size='small' color={COR_STATUS[row.status] ?? 'default'} variant='outlined' />
                </TableCell>
                <TableCell align='right'>
                  {Number(row.total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>

          {totalCount === 0 && !isLoading && (
            <caption>{Environment.LISTAGEM_VAZIA}</caption>
          )}

          <TableFooter>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <LinearProgress variant='indeterminate' />
                </TableCell>
              </TableRow>
            )}
            {(totalCount > 0 && totalCount > Environment.LIMITE_DE_LINHAS) && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Pagination
                    page={pagina}
                    count={Math.ceil(totalCount / Environment.LIMITE_DE_LINHAS)}
                    onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                  />
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </TableContainer>
    </LayoutBaseDePagina>
  );
};
